/**
 * store/search.ts — the «поиск» slice: what is in the field, and what the server answered.
 *
 * Task 10. `spec.md` «Поиск»: results appear as the listener types, with no «найти» button.
 * Its own slice for the reason `store/library.ts` gives — a list arriving here must not
 * re-render the player — and so that the query and its results survive a trip to another tab:
 * a listener who opens an artist from the results and comes back finds them where they were.
 *
 * When a request goes, which answer is drawn and what counts as «the same query» — one request
 * per pause in typing, the newest answer only, `normalizeText` — are
 * `lib/search-as-you-type.ts`'s rules, shared since task 76 with the field over «Видео».
 *
 * ## While the next answer is coming, the last one stays
 *
 * Blanking the results on every keystroke would flash «Ищу…» four times a second while the
 * listener types. The old list stays on screen, marked busy, until the new one replaces it.
 */

import { create } from 'zustand';

import { fetchSearch } from '../lib/api';
import type { ApiFailure, SearchAnswer } from '../lib/api';
import { searchAsYouType } from '../lib/search-as-you-type';

import { useSession } from './session';

export type SearchStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface SearchState {
  /** Exactly what is in the field. */
  readonly text: string;
  /** {@link SearchState.text} normalised: `''` means there is nothing to search. */
  readonly query: string;
  /** The last answer; kept while the next one loads. */
  readonly answer: SearchAnswer | undefined;
  /** The normalised query {@link SearchState.answer} belongs to. `''` when there is none. */
  readonly answeredFor: string;
  /**
   * The field's text when {@link SearchState.answer} was asked for. «Ничего не нашлось по
   * запросу …» quotes this, not the field: while the next answer loads, the field already
   * says something the old answer is not about.
   */
  readonly answeredText: string;
  readonly status: SearchStatus;
  readonly failure: ApiFailure | undefined;

  /** The field changed. Schedules a request, or clears the results when nothing is left. */
  readonly setText: (text: string) => void;
  /** Asks again for what is in the field, now. */
  readonly retry: () => void;
}

/** The state of a field with nothing to search in it. */
const IDLE = Object.freeze({
  text: '',
  query: '',
  answer: undefined,
  answeredFor: '',
  answeredText: '',
  status: 'idle',
  failure: undefined,
} as const);

const typing = searchAsYouType<SearchAnswer>({
  fetch: async (text) => await fetchSearch(useSession.getState().session?.token, text),
  answered: () => {
    const { status, answeredFor } = useSearch.getState();
    return status === 'ready' ? answeredFor : undefined;
  },
  onLoading: () => {
    useSearch.setState({ status: 'loading', failure: undefined });
  },
  onAnswer: (answer, text, query) => {
    useSearch.setState({
      answer,
      answeredFor: query,
      answeredText: text,
      status: 'ready',
      failure: undefined,
    });
  },
  onFailure: (failure) => {
    useSearch.setState({ status: 'failed', failure });
  },
});

export const useSearch = create<SearchState>((set, get) => ({
  ...IDLE,

  setText: (text) => {
    const query = typing.type(text);
    // Nothing to search: no request, no stale answer arriving later, no results.
    set(query === '' ? { ...IDLE, text } : { text, query });
  },

  retry: () => {
    typing.retry(get().text);
  },
}));

/**
 * Back to an empty field, with nothing scheduled or in flight. For tests: the store is
 * module-level and outlives every screen a test renders.
 */
export function resetSearch(): void {
  typing.abandon();
  useSearch.setState(IDLE);
}
